import {
  DeltaAction,
  OffCycleNoticeStatus,
  OffCycleRule,
  type EligibleMovementResponse,
  type NoticeResponse,
} from '../../api';

/**
 * The words the off-cycle panel uses, and the one judgement it helps a person make.
 *
 * A lookup module like [`submissionCycle`](./submissionCycle.ts) and
 * [`reviewQueue`](./reviewQueue.ts), and here for the same reason: a rule
 * appears in the panel heading, beside every waiting movement and in the notice
 * history, and "Só inclusões" spelled two ways across the three is how a screen
 * stops being trusted.
 *
 * The failure reasons are the exception and are the server's own words, because
 * "a operadora respondeu que inclusões só entram pelo arquivo mensal" is a
 * sentence a table here could not produce -- and it is the thing that gets the
 * contract's rule corrected.
 */

/** What this contract's insurer takes between cutoffs. */
export const RULE_LABELS: Record<OffCycleRule, string> = {
  [OffCycleRule.NOT_PERMITTED]: 'Só pelo arquivo mensal',
  [OffCycleRule.ADMISSIONS_ONLY]: 'Inclusões urgentes fora do ciclo',
  [OffCycleRule.ALL_MOVEMENTS]: 'Qualquer movimentação fora do ciclo',
};

/** Where one notice stands. */
export const NOTICE_STATUS_LABELS: Record<OffCycleNoticeStatus, string> = {
  [OffCycleNoticeStatus.PREPARED]: 'Gerado',
  [OffCycleNoticeStatus.SENT]: 'Comunicado',
  [OffCycleNoticeStatus.FAILED]: 'Recusado',
};

/** The movement each notice is about. Same words the review queue uses. */
export const ACTION_LABELS: Record<DeltaAction, string> = {
  [DeltaAction.ADMISSION]: 'Inclusão',
  [DeltaAction.TERMINATION]: 'Exclusão',
  [DeltaAction.SALARY_CHANGE]: 'Alteração salarial',
  [DeltaAction.LEAVE]: 'Afastamento',
};

export const OFF_CYCLE_COPY = {
  title: 'Fora do ciclo',
  description:
    'Movimentações aprovadas que a operadora ainda não conhece, e quanto falta para o arquivo ' +
    'que as levaria. Quando o contrato permite, dá para comunicar antes.',
  ruleLabel: 'O que esta operadora aceita fora do ciclo',
  waitingTitle: 'Aguardando o próximo arquivo',
  waitingDescription:
    'Cada linha é uma movimentação já aprovada que a operadora ainda não recebeu, com o ciclo ' +
    'que vai levá-la e quantos dias faltam para o corte desse ciclo.',
  noWaiting: 'Nada aguardando: a operadora já foi informada de tudo que está aprovado.',
  noticesTitle: 'Avisos fora do ciclo',
  noticesDescription:
    'Todos os avisos deste contrato, inclusive os recusados. Um aviso recusado continua aqui: ' +
    'é ele que responde se esta operadora realmente aceita movimentação fora do ciclo.',
  noNotices: 'Nenhum aviso fora do ciclo neste contrato.',

  // --- The actions ---------------------------------------------------------
  raise: 'Gerar aviso',
  markSent: 'Marcar como comunicado',
  markFailed: 'Registrar recusa',
  confirmSent: 'Confirmar comunicação',
  confirmFailed: 'Confirmar recusa',
  cancel: 'Cancelar',
  raised: 'Aviso gerado. Baixe o arquivo, envie à operadora e volte para marcar como comunicado.',
  sent: 'Comunicação registrada.',
  failed: 'Recusa registrada. As movimentações voltaram para a lista de espera.',
  downloadFile: 'Baixar arquivo do aviso',
  downloading: 'Baixando…',
  downloadFailed: 'Não foi possível baixar o arquivo do aviso. Tente de novo em instantes.',

  // --- What each state tells the analyst -----------------------------------
  notPermitted:
    'O contrato desta empresa diz que a cobertura entra pelo arquivo mensal e por mais nada. ' +
    'Avisar antes não adiantaria — e diria a quem está olhando que o caso foi resolvido. Se a ' +
    'operadora realmente aceitar aviso fora do ciclo, registre isso no contrato ' +
    '(`off_cycle_rule`); é assim que esta tela se abre.',
  admissionsOnly:
    'Esta operadora aceita inclusão urgente entre os cortes. Exclusão e alteração salarial ' +
    'esperam o arquivo mensal, que é o mesmo que acerta a fatura.',
  reasonLabel: 'Por que não pode esperar',
  reasonHint:
    'Obrigatório, e é a justificativa inteira para sair do ciclo mensal: "admitida em 25/04, o ' +
    'próximo arquivo só fecha em 20/05".',
  selectionHint:
    'Um aviso leva um tipo de movimentação. O arquivo da operadora diz quem a pessoa é e não o ' +
    'que está acontecendo com ela, então inclusão e exclusão no mesmo arquivo não diriam o que ' +
    'se está pedindo em cada linha.',
  channelLabel: 'Por onde foi comunicado',
  channelHint: 'Metade de qualquer prova; a outra metade é o comprovante abaixo.',
  referenceLabel: 'Protocolo',
  referenceHint: 'O número ou ticket que a operadora devolveu, se devolveu algum.',
  notesLabel: 'Observações',
  notesHint:
    'O que a próxima pessoa precisa saber. É aqui que cabe o caso honesto: "enviado às 09:14 ' +
    'por e-mail, sem protocolo de volta".',
  failureReasonLabel: 'Motivo da recusa',
  failureReasonHint:
    'O que a operadora disse. Além de servir para a próxima tentativa, é o que mostra se o ' +
    '`off_cycle_rule` deste contrato estava certo.',
  proofRequired:
    'Diga o que voltou: protocolo ou uma observação — pelo menos um dos dois. Um aviso fora do ' +
    'ciclo não deixa arquivo mensal atrás de si; este registro é o único que existe.',
  stillOnTheFile:
    'O aviso não substitui o arquivo mensal e não tira ninguém dele. A operadora concilia pela ' +
    'população mensal, então a pessoa avisada aparece no próximo arquivo do mesmo jeito.',
  noTransmission:
    'Nada é transmitido por aqui. Esta tela monta o arquivo e registra o que uma pessoa fez ' +
    'com ele; entregá-lo ao sistema da operadora é um trabalho à parte.',
} as const;

/**
 * Whether this notice can still be marked communicated or refused.
 *
 * A named predicate because the panel asks it in two places and the answer is
 * not "it is not sent": a refused notice is closed too, and offering the marker
 * on one would be offering to overwrite a recorded outcome.
 */
export function isOpenNotice(notice: NoticeResponse): boolean {
  return notice.status === OffCycleNoticeStatus.PREPARED;
}

/**
 * What was recorded as proof, in the order it is worth reading.
 *
 * Returns the pieces rather than a sentence so the panel can show "nothing" as
 * loudly as it shows a protocol number.
 */
export function proofOfNotice(notice: NoticeResponse): readonly string[] {
  const parts: string[] = [];
  if (notice.reference != null && notice.reference !== '') {
    parts.push(`Protocolo ${notice.reference}`);
  }
  if (notice.proof_document_id != null) {
    parts.push('Comprovante anexado');
  }
  if (notice.notes != null && notice.notes !== '') {
    parts.push(notice.notes);
  }
  return parts;
}

/**
 * How the wait reads to a person.
 *
 * The server reports a signed number of days and refuses to call it urgent,
 * which is right -- urgency is a judgement and it belongs in the notice's
 * `reason`. What the screen owes the analyst is the number in words, including
 * the case the sign covers: a cutoff already past means the movement is waiting
 * for the next file to be produced rather than for a date that has been and
 * gone.
 */
export function waitLabel(item: EligibleMovementResponse): string {
  const days = item.days_until_cutoff;
  if (days < 0) {
    return 'O corte já passou; entra no próximo arquivo a sair';
  }
  if (days === 0) {
    return 'O corte é hoje';
  }
  return days === 1 ? 'Falta 1 dia para o corte' : `Faltam ${days} dias para o corte`;
}

/**
 * The movements a person may put on one notice, given what they have picked.
 *
 * Once one movement is selected, only movements of the same action can join it
 * -- and the server refuses a mixture anyway. Disabling the others is how the
 * rule is explained before somebody hits it rather than after.
 */
export function selectableWith(
  item: EligibleMovementResponse,
  selected: readonly EligibleMovementResponse[],
): boolean {
  if (!item.is_notifiable) {
    return false;
  }
  const [first] = selected;
  return first === undefined || first.delta.action === item.delta.action;
}
